import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { API } from "../context/SiteContext";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", record: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id." });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", record: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", record: data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "pending", record: data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || "Could not check payment status." });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-24" data-testid="payment-success-page">
      <div className="max-w-2xl w-full text-center">
        {state.status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 animate-spin text-[#3A5A40] mx-auto mb-8" />
            <h1 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] mb-4">Confirming your payment…</h1>
            <p className="text-[#57534E]">This usually takes just a few seconds.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-12 h-12 text-[#3A5A40] mx-auto mb-8" strokeWidth={1.25} />
            <p className="overline text-[#9F5C45] mb-4">Payment received</p>
            <h1 className="font-serif font-light text-4xl md:text-6xl text-[#1C1917] mb-6 tracking-tight">
              Thank you. We have you.
            </h1>
            <p className="text-[#57534E] text-lg leading-relaxed mb-2">
              <strong className="font-medium text-[#1C1917]">{state.record?.package_name}</strong> · paid in full.
            </p>
            <p className="text-[#57534E] mb-12">
              A receipt and superbill have been emailed to you. Your therapist will reach out to confirm your appointment.
            </p>
            <Link to="/" data-testid="payment-back-home" className="inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide">
              <ArrowLeft className="w-4 h-4" /> Return home
            </Link>
          </div>
        )}

        {state.status === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="w-10 h-10 text-[#9F5C45] mx-auto mb-8" />
            <h1 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] mb-4">Payment is processing</h1>
            <p className="text-[#57534E] mb-8">It's taking a little longer than usual. You'll receive an email confirmation when it clears — usually within a few minutes.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-sm tracking-wide">Return home</Link>
          </div>
        )}

        {(state.status === "error" || state.status === "expired") && (
          <div data-testid="payment-error">
            <AlertCircle className="w-10 h-10 text-[#9F5C45] mx-auto mb-8" />
            <h1 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] mb-4">Something went sideways</h1>
            <p className="text-[#57534E] mb-8">{state.error || "Your session expired before payment was completed."}</p>
            <Link to="/#pay" className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-sm tracking-wide">Try again</Link>
          </div>
        )}
      </div>
    </main>
  );
}
